/** biome-ignore-all lint/style/noRestrictedImports: <explanation> */
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { BlogPostListingInterface } from 'common/types/entities/BlogPostListingInterface'
import { ContentBoxInterface } from 'common/types/entities/ContentBoxInterface'
import { ContentTableInterface } from 'common/types/entities/ContentTableInterface'
import { LatestBlogPostsInterface } from 'common/types/entities/LatestBlogPostsInterface'
import { OfferPriceInterface } from 'common/types/entities/OfferPriceInterface'
import { SurveyInterface } from 'common/types/entities/SurveyInterface'
import { SearchResultsInterface } from 'common/types/entities/search-results-interface'
import { GlobalSettingsInterface } from 'common/types/Page'
import { CustomFontProperties } from 'common/utils/fontsUtils'
import { getNewBlogPostListingCustomFontProperties } from 'src/shared/entities/blog-post-listing/utils/get-blog-post-listing-font-properties'
import { getNewButtonCustomFontProperties } from 'src/shared/entities/button/utils/get-button-font-properties'
import { getContentBoxCustomFontProperties } from 'src/shared/entities/content-box/utils/get-content-box-font-properties'
import { getContentTableCustomFontProperties } from 'src/shared/entities/content-table/utils/get-content-table-font-properties'
import { FaqInterface } from 'src/shared/entities/faq/faq.schema'
import { getFaqCustomFontProperties } from 'src/shared/entities/faq/utils/get-faq-font-properties'
import { getFormCustomFontProperties } from 'src/shared/entities/form/utils/get-form-font-properties'
import { getOfferPriceCustomFontProperties } from 'src/shared/entities/offer-price/utils/get-offer-price-font-properties'
import { getSearchCustomFontProperties } from 'src/shared/entities/search/utils/get-search-font-properties'
import { getSearchResultsCustomFontProperties } from 'src/shared/entities/search-results/utils/get-search-results-font-properties'
import { getSurveyCustomFontProperties } from 'src/shared/entities/survey/utils/get-survey-font-properties'
import { removeDuplicates } from 'src/shared/utils/remove-duplicates'
import { FormInterface } from '../entities/form/form.schema'
import { CommonButtonEntityInterface } from '../schemas/buttons/common-button-entity.schema'
import {
  BaseTextContentEntityInterface,
  PageEntityInterface,
} from '../types/base-entity-interface'
import { DataFile } from '../types/data-file-interface'
import { SearchInterface } from '../types/entities/search-interface'

export class CustomFontsGenerator {
  public generate(
    entities: PageEntityInterface[],
    files: DataFile[],
    globalFontsProperties: CustomFontProperties[] = [],
  ) {
    const entitiesFontsProperties =
      this.getEntitiesCustomFontsProperties(entities)
    const pageCustomFonts = [
      ...entitiesFontsProperties,
      ...globalFontsProperties,
    ]

    return this.generateCustomFontFaces(pageCustomFonts, files)
  }

  private getEntitiesCustomFontsProperties(entities: PageEntityInterface[]) {
    return entities.flatMap(entity => this.getCustomFontProperties(entity))
  }

  private getCustomFontProperties(
    entity: PageEntityInterface,
  ): CustomFontProperties[] {
    switch (entity.type) {
      case EntityTypeEnum.ContentTable:
        return getContentTableCustomFontProperties(
          entity as ContentTableInterface,
        )
      case EntityTypeEnum.ContentBox:
        return getContentBoxCustomFontProperties(entity as ContentBoxInterface)
      case EntityTypeEnum.Faq:
        return getFaqCustomFontProperties(entity as FaqInterface)
      case EntityTypeEnum.OfferPrice:
        return getOfferPriceCustomFontProperties(entity as OfferPriceInterface)
      case EntityTypeEnum.Button:
      case EntityTypeEnum.ContactUsButton:
      case EntityTypeEnum.PaymentButton:
      case EntityTypeEnum.UpsellAgreeButton:
      case EntityTypeEnum.UpsellDisagreeButton:
      case EntityTypeEnum.WebinarCallToAction:
      case EntityTypeEnum.SalesRedirectionButton:
        return getNewButtonCustomFontProperties(
          entity as CommonButtonEntityInterface,
        )
      case EntityTypeEnum.Survey:
        return getSurveyCustomFontProperties(entity as SurveyInterface)
      case EntityTypeEnum.BlogPostListing:
      case EntityTypeEnum.LatestBlogPosts:
        return getNewBlogPostListingCustomFontProperties(
          entity as BlogPostListingInterface | LatestBlogPostsInterface,
        )
      case EntityTypeEnum.SearchResults:
        return getSearchResultsCustomFontProperties(
          entity as SearchResultsInterface,
        )
      case EntityTypeEnum.Search:
        return getSearchCustomFontProperties(entity as SearchInterface)
      case EntityTypeEnum.Form:
        return getFormCustomFontProperties(entity as FormInterface)
      default:
        return CustomFontsGenerator.getBaseCustomFontProperties(
          entity as BaseTextContentEntityInterface,
        )
    }
  }

  private generateCustomFontFaces(
    fonts: CustomFontProperties[],
    files: DataFile[],
  ) {
    const uniqueFonts = removeDuplicates(fonts, ['fontFamily', 'fontFileId'])

    return uniqueFonts
      .map(font => {
        const fontFile = files.find(file => font.fontFileId === file.id)
        return fontFile
          ? `@font-face {
            font-family: ${font.fontFamily};
            src: url('${fontFile.path}');
          }`
          : ''
      })
      .join('\n')
      .trim()
  }

  static getBaseCustomFontProperties(
    entity: Pick<
      BaseTextContentEntityInterface,
      'fontFamily' | 'fontFileId' | 'mobileFontFamily' | 'mobileFontFileId'
    >,
  ) {
    const fontProperties = []

    if (entity.fontFamily && entity.fontFileId) {
      fontProperties.push({
        fontFamily: entity.fontFamily,
        fontFileId: entity.fontFileId,
      })
    }

    if (entity.mobileFontFamily && entity.mobileFontFileId) {
      fontProperties.push({
        fontFamily: entity.mobileFontFamily,
        fontFileId: entity.mobileFontFileId,
      })
    }

    return fontProperties
  }
}

export class PageCustomFontsGenerator extends CustomFontsGenerator {
  public generateFonts(
    entities: PageEntityInterface[],
    files: DataFile[],
    globalSettings: GlobalSettingsInterface,
  ) {
    const globalSettingsFonts =
      this.getGlobalSettingsCustomFontProperties(globalSettings)
    return super.generate(entities, files, globalSettingsFonts)
  }

  private getGlobalSettingsCustomFontProperties = (
    globalSettings: GlobalSettingsInterface = {},
  ): CustomFontProperties[] => {
    const fontProperties: CustomFontProperties[] = []

    if (globalSettings.fontFamily && globalSettings.fontFileId) {
      fontProperties.push({
        fontFamily: globalSettings.fontFamily,
        fontFileId: globalSettings.fontFileId,
      })
    }

    if (globalSettings.headingFontFamily && globalSettings.headingFontFileId) {
      fontProperties.push({
        fontFamily: globalSettings.headingFontFamily,
        fontFileId: globalSettings.headingFontFileId,
      })
    }

    return fontProperties
  }
}

export class WebsiteCustomFontsGenerator extends CustomFontsGenerator {
  public generateFonts(
    entities: PageEntityInterface[],
    files: DataFile[],
    websiteStylesTypographies: Partial<CustomFontProperties>[],
  ) {
    const websiteStylesFonts = this.getWebsiteStyleCustomFontProperties(
      websiteStylesTypographies,
    )
    return super.generate(entities, files, websiteStylesFonts)
  }

  private getWebsiteStyleCustomFontProperties = (
    websiteStylesTypographies: Partial<CustomFontProperties>[] = [],
  ): CustomFontProperties[] => {
    const fontProperties: CustomFontProperties[] = []

    websiteStylesTypographies.forEach(typography => {
      if (typography.fontFamily && typography.fontFileId) {
        fontProperties.push({
          fontFamily: typography.fontFamily,
          fontFileId: typography.fontFileId,
        })
      }
    })

    return fontProperties
  }
}
